import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { apiFetch, openAuthenticatedUpload } from '../api/client';

const STATUS_OPTIONS = [
  { value: 'TODOS', label: 'Todos' },
  { value: 'PENDENTE', label: 'Pendentes' },
  { value: 'EM_ANDAMENTO', label: 'Em andamento' },
  { value: 'CONCLUIDO', label: 'Concluídos' },
  { value: 'CANCELADO', label: 'Cancelados' }
];

const TIPO_LABELS = {
  ATESTO_AVALIACAO: 'Atesto / Avaliação',
  PROCESSO_PAGAMENTO: 'Processo de pagamento',
  CONTRATACAO: 'Contratação',
  ADITIVO: 'Aditivo',
  DIVERSOS: 'Diversos'
};

const statusColor = {
  PENDENTE: 'warning',
  EM_ANDAMENTO: 'info',
  CONCLUIDO: 'success',
  CANCELADO: 'error'
};

function formatarData(dataIso) {
  if (!dataIso) return '-';
  return new Date(dataIso).toLocaleString('pt-BR');
}

/**
 * Lista de documentos de assinatura.
 * @param {{ tipoFixo?: string, tituloPagina?: string, mostrarFiltroSetor?: boolean }} props
 */
export default function RegulamentoAssinaturasDocumentosPage({
  tipoFixo = null,
  tituloPagina = 'Documentos para Assinatura',
  mostrarFiltroSetor = false,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { unidadeSelecionada, user } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setErro] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('TODOS');
  const [setorFiltro, setSetorFiltro] = useState(searchParams.get('setor') || 'TODOS');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const tipoEfetivo = tipoFixo || searchParams.get('tipo') || null;

  const carregarDocumentos = async () => {
    try {
      setLoading(true);
      setErro('');
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      if (tipoEfetivo) params.set('tipo', tipoEfetivo);
      if (setorFiltro && setorFiltro !== 'TODOS') params.set('setor', setorFiltro);
      if (statusFiltro && statusFiltro !== 'TODOS') params.set('status', statusFiltro);
      const qs = params.toString();
      const response = await apiFetch(qs ? `/api/assinaturas/documentos?${qs}` : '/api/assinaturas/documentos');
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao listar documentos.');
      }
      setDocumentos(data || []);
    } catch (error) {
      console.error('Erro ao carregar documentos de assinatura:', error);
      setErro(error.message || 'Erro ao carregar documentos.');
      setSnackbar({ open: true, message: error.message || 'Erro ao carregar documentos.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDocumentos();
  }, [unidadeSelecionada?.codigo, tipoEfetivo, setorFiltro, statusFiltro]);

  const setoresDisponiveis = useMemo(() => {
    const set = new Set();
    documentos.forEach((d) => {
      if (d.setor) set.add(d.setor);
    });
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [documentos]);

  const documentosFiltrados = useMemo(() => {
    return documentos.filter((doc) => {
      if (!searchTerm.trim()) return true;
      const termo = searchTerm.trim().toLowerCase();
      return (
        (doc.titulo || '').toLowerCase().includes(termo) ||
        (doc.criadoPorNome || '').toLowerCase().includes(termo) ||
        (doc.setor || '').toLowerCase().includes(termo) ||
        doc.participantes?.some((p) => (p.nome || '').toLowerCase().includes(termo))
      );
    });
  }, [documentos, searchTerm]);

  const totalPorStatus = useMemo(() => {
    return documentos.reduce(
      (acc, doc) => {
        const status = (doc.status || 'PENDENTE').toUpperCase();
        acc[status] = (acc[status] || 0) + 1;
        acc.TOTAL += 1;
        return acc;
      },
      { TOTAL: 0, PENDENTE: 0, EM_ANDAMENTO: 0, CONCLUIDO: 0, CANCELADO: 0 }
    );
  }, [documentos]);

  const subtitulo = tipoEfetivo
    ? `Pasta: ${TIPO_LABELS[tipoEfetivo] || tipoEfetivo}. Você vê documentos que criou ou em que participa (até concluir).`
    : 'Você vê o que criou ou em que participa. O Setor de Contratos visualiza todos da unidade.';

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
              {tituloPagina}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitulo}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={carregarDocumentos}
              disabled={loading}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: 'none' }}
              onClick={() =>
                navigate('/assinaturas/novo', {
                  state: tipoEfetivo ? { tipoDocumento: tipoEfetivo } : undefined,
                })
              }
            >
              Novo Documento
            </Button>
          </Stack>
        </Box>

        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(25,118,210,0.12)' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Total: ${totalPorStatus.TOTAL}`} color="primary" variant="outlined" />
              <Chip label={`Pendentes: ${totalPorStatus.PENDENTE || 0}`} color="warning" variant="outlined" />
              <Chip label={`Em andamento: ${totalPorStatus.EM_ANDAMENTO || 0}`} color="info" variant="outlined" />
              <Chip label={`Concluídos: ${totalPorStatus.CONCLUIDO || 0}`} color="success" variant="outlined" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexShrink={0}>
              <TextField
                label="Pesquisar"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <TextField
                select
                label="Status"
                value={statusFiltro}
                onChange={(e) => setStatusFiltro(e.target.value)}
                size="small"
                sx={{ minWidth: 160 }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              {(mostrarFiltroSetor || tipoEfetivo === 'ATESTO_AVALIACAO') && (
                <TextField
                  select
                  label="Setor"
                  value={setorFiltro}
                  onChange={(e) => setSetorFiltro(e.target.value)}
                  size="small"
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="TODOS">Todos os setores</MenuItem>
                  {setoresDisponiveis.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                  {user?.setor && !setoresDisponiveis.includes(user.setor) && (
                    <MenuItem value={user.setor}>{user.setor}</MenuItem>
                  )}
                </TextField>
              )}
            </Stack>
          </Stack>
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Documento</TableCell>
                  {!tipoEfetivo && <TableCell>Tipo</TableCell>}
                  <TableCell>Setor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progresso</TableCell>
                  <TableCell>Próximo</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tipoEfetivo ? 7 : 8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nenhum documento nesta pasta.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  documentosFiltrados.map((doc) => (
                    <TableRow
                      key={doc.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/assinaturas/documentos/${doc.id}`)}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DescriptionOutlinedIcon color="primary" fontSize="small" />
                          <Typography variant="body2" fontWeight={600}>
                            {doc.titulo}
                          </Typography>
                        </Stack>
                      </TableCell>
                      {!tipoEfetivo && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={TIPO_LABELS[doc.tipo] || doc.tipo || '—'}
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell>{doc.setor || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={statusColor[doc.status] || 'default'}
                          label={doc.status}
                        />
                      </TableCell>
                      <TableCell>
                        {doc.resumo
                          ? `${doc.resumo.assinados}/${doc.resumo.total}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {doc.resumo?.proximo?.nome ||
                          (doc.status === 'CONCLUIDO' ? '—' : '—')}
                      </TableCell>
                      <TableCell>{formatarData(doc.criadoEm)}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Abrir">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/assinaturas/documentos/${doc.id}`)}
                          >
                            <DescriptionOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(doc.arquivoUrl || doc.arquivoAtualPath) && (
                          <Tooltip title="Baixar PDF">
                            <IconButton
                              size="small"
                              onClick={() =>
                                openAuthenticatedUpload(doc.arquivoUrl || doc.arquivoAtualPath)
                              }
                            >
                              <DownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
